import React from 'react';
import { motion } from 'framer-motion';
import { Tag, Tooltip } from 'antd';

import { EditableStatusInput } from '@partsbadger/ui-library';

interface IStepCardProps {
    stepInfo: {
        id: number;
        name: string;
        materialType?: string;
        colored?: boolean;
    };
    entity: 'material' | 'finish' | 'coating';
    selectedId?: number | null;
    onSelect: (id: number) => void;
    className?: string;
    customValue: string | null;
    onUpdateCustom: (value: string) => void;
    isPopular?: boolean;
    disabled?: boolean;
    disabledReason?: string;
}

export const StepCard: React.FC<IStepCardProps> = ({
    stepInfo,
    entity,
    selectedId,
    onSelect,
    className = '',
    customValue,
    onUpdateCustom,
    isPopular = false,
    disabled = false,
    disabledReason,
}) => {
    const isSelected = selectedId === stepInfo.id;
    const isCustomMissing = stepInfo.name === 'Custom' && isSelected && (!customValue || customValue.trim() === '');

    let buttonSurfaceClass = 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md';
    if (disabled) {
        buttonSurfaceClass = 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed';
    } else if (isCustomMissing) {
        buttonSurfaceClass = 'border-red-500 bg-red-50/80 shadow-lg ring-2 ring-red-500/25';
    } else if (isSelected) {
        buttonSurfaceClass = 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-500/20';
    }

    const cardDiscriminatorAttributes = {
        [`data-${entity}-id`]: stepInfo.id,
        [`data-${entity}-name`]: stepInfo.name,
        ...(entity === 'material' && stepInfo.materialType
            ? { 'data-material-type': stepInfo.materialType }
            : {}),
        ...(entity === 'material' && typeof stepInfo.colored === 'boolean'
            ? { 'data-material-colored': String(stepInfo.colored) }
            : {}),
    };

    const card = (
        <motion.div
            key={stepInfo.id}
            whileHover={disabled ? undefined : { scale: 1.01, transition: { duration: 0.2 } }}
            whileTap={disabled ? undefined : { scale: 0.99 }}
            className={`step-card relative ${className}`}
            layout
        >
            <button
                type="button"
                data-testid={`${entity}-card`}
                aria-pressed={isSelected}
                aria-invalid={isCustomMissing}
                {...cardDiscriminatorAttributes}
                aria-disabled={disabled}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        onSelect(stepInfo.id);
                    }
                }}
                className={`w-full text-left p-2 rounded-lg transition-all duration-300 ${buttonSurfaceClass}`}
                style={{
                    borderWidth: isSelected || isCustomMissing ? '2px' : '1px',
                }}
            >
                <div className="flex items-center space-x-3">
                    <div className="flex-1">
                        <div className="text-sm font-normal ">{stepInfo.name}</div>
                        {stepInfo.name === 'Custom' && selectedId === stepInfo.id && (
                            <EditableStatusInput
                                initialValue={customValue || ''}
                                onSave={value => {
                                    onUpdateCustom(value);
                                }}
                            />
                        )}
                    </div>

                    {isPopular && (
                        <Tag color="green" className="m-0 rounded-full border-0">
                            Popular
                        </Tag>
                    )}
                </div>
            </button>
        </motion.div>
    );

    if (disabled && disabledReason) {
        return <Tooltip title={disabledReason}>{card}</Tooltip>;
    }

    return card;
};
